import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import queryString from 'query-string';

import { useToast } from '../../../redux/actions/UI';
import { InvalidDataError } from '../api';
import {
  useCreateCampaignMutation,
  useAutosaveCampaignMutation,
} from '../queries';
import CampaignEditor from '../CampaignEditor';
import useCampaignAutosave from '../hooks/useCampaignAutosave';
import { useCampaignEditorLogic } from '../hooks/useCampaignEditorLogic';
import generateObjectId from '../utils/generateObjectId';

const CreateCampaign = () => {
  const history = useHistory();
  const { successToast, errorToast } = useToast();
  const { mutateAsync: createCampaign } = useCreateCampaignMutation();
  const { mutateAsync: autosaveCampaign } = useAutosaveCampaignMutation();

  // Track campaign ID - will be set when campaign is first created
  const [campaignId, setCampaignId] = useState(null);
  const [currentCampaignData, setCurrentCampaignData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const isInitialLoadRef = useRef(true);
  const hasCreatedCampaignRef = useRef(false);
  const createCampaignPromiseRef = useRef(null); // Track ongoing campaign creation to prevent duplicates
  const pendingCreationRef = useRef(false); // Set by handleTouch, consumed by handleDataChange

  // Helper function to ensure at least one stage exists with an ID
  const ensureStagesHaveIds = useCallback(stages => {
    if (!stages || stages.length === 0) {
      // Create a default stage if none exist
      return [
        {
          _id: generateObjectId(),
          name: 'Stage 1',
          conditions: [
            {
              _id: generateObjectId(),
              name: null,
              delay: { value: 0, order: 'after', unit: 'day' },
            },
          ],
        },
      ];
    }

    // Ensure all stages have IDs
    return stages.map(stage => {
      if (!stage._id) {
        return {
          ...stage,
          _id: generateObjectId(),
          conditions: (stage.conditions || []).map(condition => ({
            ...condition,
            _id: condition._id || generateObjectId(),
          })),
        };
      }
      // Ensure conditions have IDs
      if (stage.conditions) {
        return {
          ...stage,
          conditions: stage.conditions.map(condition => ({
            ...condition,
            _id: condition._id || generateObjectId(),
          })),
        };
      }
      return stage;
    });
  }, []);

  // Helper function to create a minimal campaign object
  const createMinimalCampaign = useCallback(
    campaignData => {
      const stagesWithIds = ensureStagesHaveIds(campaignData.stages);
      return {
        name: campaignData.name || 'New Campaign',
        description: campaignData.description || '',
        status: 'unfinished', // Use unfinished status for new/incomplete campaigns
        audience: campaignData.audience || {
          name: '', // Empty name is allowed for unfinished campaigns
          description: '',
          topicIds: [],
          selectedGroupIds: [],
          requiredGroupIds: [],
          excludedGroupIds: [],
          includedMemberIds: [],
          excludedMemberIds: [],
          filterData: {},
        },
        stages: stagesWithIds,
      };
    },
    [ensureStagesHaveIds]
  );

  // Helper function to handle campaign creation promise
  const createCampaignPromise = useCallback(
    (
      campaignData,
      minimalCampaign,
      errorMessage = 'Failed to create campaign:',
      onSuccess
    ) => {
      return createCampaign(minimalCampaign)
        .then(createdCampaign => {
          setCampaignId(createdCampaign._id);
          // Update current data with the created campaign data.
          // Use backend-returned stages so local ObjectIds are replaced with
          // the IDs the backend assigned — keeping editor state in sync with the DB.
          const updatedData = {
            ...campaignData,
            _id: createdCampaign._id,
            stages: createdCampaign.stages || campaignData.stages,
          };
          setCurrentCampaignData(updatedData);

          // Update URL to include campaign ID so TemplateEditor can access it via useParams.
          // Preserve existing query params (e.g. ?step=template&stageId=xxx set by Stage.js)
          // but remap stageId from local ObjectId → backend-assigned stage ID (matched by position),
          // so EditCampaign's initialStep and updateStageTemplate both use the correct ID.
          let updatedSearch = history.location.search;
          if (createdCampaign.stages?.length && campaignData.stages?.length) {
            const params = queryString.parse(history.location.search);
            if (params.stageId) {
              const localIndex = campaignData.stages.findIndex(
                s => s._id === params.stageId
              );
              if (localIndex !== -1 && createdCampaign.stages[localIndex]) {
                params.stageId = createdCampaign.stages[localIndex]._id;
                updatedSearch = `?${queryString.stringify(params)}`;
              }
            }
          }
          history.replace(
            `/dashboard/automation/edit/${createdCampaign._id}${updatedSearch}`
          );

          // Call optional success callback
          if (onSuccess) {
            onSuccess(createdCampaign);
          }

          createCampaignPromiseRef.current = null;
          return createdCampaign;
        })
        .catch(error => {
          console.error(errorMessage, error);
          // Reset ref on error so we can retry
          hasCreatedCampaignRef.current = false;
          createCampaignPromiseRef.current = null;
          throw error;
        });
    },
    [history]
  );

  // Handle autosave errors
  const handleAutosaveError = useCallback(
    error => {
      console.error('Autosave error:', error);
      // Don't show error toast for InvalidDataError - allow saving regardless of validation
      if (!(error instanceof InvalidDataError)) {
        errorToast(
          'Failed to autosave campaign. Changes may be lost if you leave this page.',
          5
        );
      }
    },
    [errorToast]
  );

  // Handle autosave success - update URL with campaign ID if needed
  const handleAutosaveSuccess = useCallback(
    (savedCampaignId, _savedData) => {
      // Update URL if we have a campaign ID but URL doesn't have it yet
      const currentPath = history.location.pathname;
      const expectedPath = `/dashboard/automation/edit/${savedCampaignId}`;

      if (savedCampaignId && currentPath !== expectedPath) {
        history.replace(expectedPath);
      }
    },
    [history]
  );

  // Setup autosave hook - only works after campaign is created
  const { saveNow, saveStatus, hasFailed } = useCampaignAutosave({
    currentData: currentCampaignData,
    campaignId: campaignId, // Will be null initially, then set after creation
    onSave: autosaveCampaign,
    onError: handleAutosaveError,
    onSaveSuccess: handleAutosaveSuccess,
    isDirty,
  });

  // Custom logic for CreateCampaign: handle campaign creation before navigation
  const handleBeforeStepChangeCustom = useCallback(
    async (_newStep, _params, editorData) => {
      // If campaign doesn't exist yet, create it now before navigation
      // This ensures the campaign exists in the database and can be referenced
      if (
        !campaignId &&
        !hasCreatedCampaignRef.current &&
        !createCampaignPromiseRef.current &&
        editorData
      ) {
        // Set ref immediately to prevent duplicate creation if called multiple times
        hasCreatedCampaignRef.current = true;

        // Ensure stages have IDs so templates can be applied later
        const minimalCampaign = createMinimalCampaign(editorData);

        // Store the promise to prevent concurrent creation
        const createPromise = createCampaignPromise(
          editorData,
          minimalCampaign,
          'Failed to create campaign before navigation:',
          () => {
            setIsDirty(false); // Reset dirty flag since we just saved
          }
        );

        createCampaignPromiseRef.current = createPromise;

        // Wait for campaign creation to complete before allowing navigation
        // If promise rejects, error is handled in createCampaignPromise catch handler
        // which clears the ref, allowing retry on next attempt
        await createPromise;
      } else if (createCampaignPromiseRef.current) {
        // If campaign creation is in progress, wait for it to complete
        try {
          await createCampaignPromiseRef.current;
        } catch (error) {
          // If promise rejected, ref should be cleared by catch handler
          // But check if ref is still set (race condition) and clear it
          if (createCampaignPromiseRef.current) {
            createCampaignPromiseRef.current = null;
            hasCreatedCampaignRef.current = false;
          }
          // Re-throw to prevent navigation on error
          throw error;
        }
      } else if (isDirty && currentCampaignData && campaignId) {
        // Campaign exists, save changes if dirty
        try {
          await saveNow();
          setIsDirty(false);
        } catch (error) {
          // If save fails, still allow navigation
          console.warn('Failed to save before step change:', error);
        }
      }
    },
    [campaignId, currentCampaignData, isDirty]
  );

  // Use shared editor logic hook
  const { handleBeforeStepChange: sharedHandleBeforeStepChange } =
    useCampaignEditorLogic({
      saveNow,
      hasFailed,
      currentCampaignData,
      onBeforeStepChangeCustom: handleBeforeStepChangeCustom,
    });

  // Handle touch (user makes changes) - signals that campaign creation should happen on next data change
  const handleTouch = useCallback(() => {
    setIsDirty(true);

    // Mark that creation is pending - handleDataChange will pick this up with the latest data
    if (
      !hasCreatedCampaignRef.current &&
      !campaignId &&
      !createCampaignPromiseRef.current
    ) {
      pendingCreationRef.current = true;
    }
  }, [campaignId]);

  // Wrap shared handleBeforeStepChange to include custom logic
  const handleBeforeStepChange = useCallback(
    async (_newStep, _params, editorData) => {
      // Run custom logic first (campaign creation)
      await handleBeforeStepChangeCustom(_newStep, _params, editorData);
      // Then run shared validation logic
      await sharedHandleBeforeStepChange(_newStep, _params, editorData);
    },
    []
  );

  // Handle data changes from CampaignEditor - creates campaign if a touch has been registered
  const handleDataChange = useCallback(
    async data => {
      setCurrentCampaignData(data);

      // If handleTouch signalled that the user has interacted, create the campaign now
      // using the latest data (which includes the user's just-made change)
      if (
        pendingCreationRef.current &&
        !hasCreatedCampaignRef.current &&
        !createCampaignPromiseRef.current
      ) {
        pendingCreationRef.current = false;
        hasCreatedCampaignRef.current = true;
        const minimalCampaign = createMinimalCampaign(data);
        const createPromise = createCampaignPromise(
          data,
          minimalCampaign,
          'Failed to create campaign:'
        );
        createCampaignPromiseRef.current = createPromise;
      }

      // Don't mark as dirty on initial load - only when user makes changes
      if (!isInitialLoadRef.current) {
        setIsDirty(true);
      } else {
        // Mark initial load as complete after first data change
        isInitialLoadRef.current = false;
      }
    },
    [createMinimalCampaign, createCampaignPromise]
  );

  const handleConfirm = async (campaignData, shouldTriggerConfetti = false) => {
    try {
      // Campaign should already exist (created on first user interaction or in handleBeforeStepChange)
      // Just save final changes and navigate
      if (isDirty) {
        await saveNow();
      }
      successToast('Campaign successfully updated');
      // Pass confetti trigger flag through location state
      history.push('/dashboard/automation', {
        triggerConfetti: shouldTriggerConfetti,
      });
    } catch (error) {
      console.error(error);
      // Don't show error toast for InvalidDataError - allow saving regardless of validation
      if (!(error instanceof InvalidDataError)) {
        errorToast(
          'Failed to create campaign. Please, try again later. If the issue persists, please contact us.',
          10
        );
      }
    }
  };

  // Initialize campaign data when component mounts.
  // Include a default stage so Stages.js doesn't auto-call addStage() on mount,
  // which would trigger onTouch → pendingCreationRef and create a campaign before any user interaction.
  const initialData = useMemo(() => {
    const defaultName = `New Campaign ${moment().format(
      'MM-DD-YYYY HH:mm:ss'
    )}`;
    return {
      status: 'unfinished',
      name: defaultName,
      stages: [
        {
          _id: generateObjectId(),
          name: 'Stage 1',
          conditions: [
            {
              _id: generateObjectId(),
              name: null,
              delay: { value: 0, order: 'after', unit: 'day' },
            },
          ],
        },
      ],
    };
  }, []);

  return (
    <>
      <CampaignEditor
        initialData={initialData}
        confirmText="Confirm and Create"
        onConfirm={handleConfirm}
        onTouch={handleTouch}
        onDataChange={handleDataChange}
        onBeforeStepChange={handleBeforeStepChange}
        autosaveStatus={saveStatus}
      />
    </>
  );
};

export default CreateCampaign;
